import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LogOut, Search, ArrowUpRight, Shield, Sparkles } from "lucide-react";
import Logo from "./Logo";
import HeaderTextZoom from "./HeaderTextZoom";
import { ThemeToggle } from "./ThemeToggle";
import { SSO_ENABLED, useSsoSession, erioLoginUrl } from "../lib/ssoSession";
import { useBranding } from "../lib/branding";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/the-360-family", label: "The 360 Family" },
  { to: "/methodology", label: "Methodology" },
  { to: "/start", label: "Start here" },
  { to: "/value-stream-assessment", label: "Assess" },
  { to: "/insights", label: "Insights" },
  { to: "/case-studies", label: "Case studies" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { signedIn, name, isAdmin, hasTools } = useSsoSession();
  const branding = useBranding();
  // Show "Sign in" only once ERIO's shared-tenant OIDC is live (env flag),
  // so visitors never hit an unconfigured endpoint. A signed-in visitor
  // always sees their name + Sign out.
  const showSignIn = SSO_ENABLED && !signedIn;
  // The admin dashboard renders inside this Layout and has its OWN Sign out,
  // so suppress the header's signed-in actions there to avoid duplicates.
  const onAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 bg-black border-b border-white/10"
    >
      {/* Cross-app utility bar — mirrors ERIO's "Back to Ekselens Consulting"
          strip so the two products read as one family. */}
      <div className="bg-[#0A0A0C] border-b border-white/[0.08]">
        <div className="w-full px-4 md:px-10 h-9 flex items-center justify-center">
          <a
            href={erioLoginUrl()}
            data-testid="header-erio-link"
            className="group inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-white/55 hover:text-white transition-colors"
            aria-label="Open ERIO — the Ekselens risk-intelligence platform"
          >
            Open ERIO — risk intelligence
            <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>

      <div className="w-full pl-4 pr-6 md:pl-6 md:pr-10 flex items-center gap-4 h-[96px]">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <Logo variant="on-dark" size="md" logoUrl={branding.logo_url} brandName={branding.name} color={branding.primary_color} />
          <span className="sr-only">{branding.name}</span>
        </Link>

        <nav className="hidden md:flex flex-1 items-center justify-center gap-4 lg:gap-7" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) => {
                const base = "text-[13px] font-medium tracking-[0.12em] uppercase transition-colors";
                return `${base} ${isActive ? "text-[#A179FA]" : "text-white/85 hover:text-white"}`;
              }}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <HeaderTextZoom />
          <ThemeToggle className="text-white/85 hover:text-white" />
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("ek-open-search"))}
            className="text-white/85 hover:text-white p-2.5 transition-colors"
            aria-label="Search"
            title="Search (⌘K)"
            data-testid="header-search-button"
          >
            <Search size={18} />
          </button>
          {signedIn && !onAdmin && (
            <div className="flex items-center gap-2" data-testid="header-signed-in">
              {hasTools && !isAdmin && (
                <Link
                  to="/tools"
                  className="text-[12.5px] tracking-[0.12em] uppercase text-[#A179FA] hover:text-white border border-[#7737F6]/50 hover:border-white px-4 py-2.5 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="header-tools-link"
                  aria-label="Open the EKSELENS360 evaluation workspace"
                >
                  <Sparkles size={13} /> Tools
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-[12.5px] tracking-[0.12em] uppercase text-[#A179FA] hover:text-white border border-[#7737F6]/50 hover:border-white px-4 py-2.5 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="header-admin-link"
                  aria-label="Open the admin dashboard"
                >
                  <Shield size={13} /> Admin
                </Link>
              )}
              <span className="text-[12px] text-white/70 max-w-[140px] truncate" data-testid="header-user-name">{name || "Signed in"}</span>
              <Link
                to="/logout"
                className="text-[12.5px] tracking-[0.12em] uppercase text-white/85 hover:text-white border border-white/30 hover:border-white px-4 py-2.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="ekselens-nav-signout"
                aria-label="Sign out of ERIO and Ekselens"
              >
                <LogOut size={13} /> Sign out
              </Link>
            </div>
          )}
          {showSignIn && (
            <Link
              to="/signin"
              className="text-[12.5px] tracking-[0.12em] uppercase text-white/85 hover:text-white border border-white/30 hover:border-white px-4 py-2.5 inline-flex items-center gap-1.5 transition-colors"
              data-testid="header-sign-in-erio"
              title="Sign in via shared Auth0 single sign-on."
            >
              <LogIn size={13} /> Sign in
            </Link>
          )}
          <Link
            to="/contact"
            className="ek-btn ek-btn-primary text-[12.5px] tracking-[0.12em] uppercase px-6 py-3"
            data-testid="header-cta-book-discovery"
          >
            Book a discovery conversation
          </Link>
        </div>

        <div className="md:hidden flex items-center ml-auto">
          <button
            type="button"
            className="p-2 text-white"
            aria-label="Search"
            onClick={() => window.dispatchEvent(new CustomEvent("ek-open-search"))}
            data-testid="header-mobile-search"
          >
            <Search size={20} />
          </button>
          <button
            type="button"
            className="p-2 text-white"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="header-mobile-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-black" data-testid="mobile-menu">
          <div className="ek-container py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium py-1 ${
                    isActive ? "text-[#A179FA]" : "text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {signedIn && !onAdmin && (
              <>
                {hasTools && !isAdmin && (
                  <Link
                    to="/tools"
                    onClick={() => setMobileOpen(false)}
                    className="text-[#A179FA] border border-[#7737F6]/50 px-4 py-2.5 mt-2 text-[13px] tracking-[0.12em] uppercase inline-flex items-center gap-1.5"
                    data-testid="header-tools-link-mobile"
                  >
                    <Sparkles size={13} /> Tools
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="text-[#A179FA] border border-[#7737F6]/50 px-4 py-2.5 mt-2 text-[13px] tracking-[0.12em] uppercase inline-flex items-center gap-1.5"
                    data-testid="header-admin-link-mobile"
                  >
                    <Shield size={13} /> Admin
                  </Link>
                )}
                <Link
                  to="/logout"
                  onClick={() => setMobileOpen(false)}
                  className="text-white/90 border border-white/30 px-4 py-2.5 mt-2 text-[13px] tracking-[0.12em] uppercase inline-flex items-center gap-1.5"
                  data-testid="ekselens-nav-signout-mobile"
                  aria-label="Sign out of ERIO and Ekselens"
                >
                  <LogOut size={13} /> Sign out{name ? ` (${name})` : ""}
                </Link>
              </>
            )}
            {showSignIn && (
              <Link
                to="/signin"
                className="text-white/90 border border-white/30 px-4 py-2.5 mt-2 text-[13px] tracking-[0.12em] uppercase inline-flex items-center gap-1.5"
                data-testid="mobile-sign-in-erio"
                onClick={() => setMobileOpen(false)}
              >
                <LogIn size={13} /> Sign in
              </Link>
            )}
            <Link to="/contact" className="ek-btn ek-btn-primary mt-2" data-testid="mobile-cta-book-discovery">
              Book a discovery conversation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
